import { FeedbackState } from "@c/shared";
import { cn } from "@u/cn";
import { Checkbox } from "@ui/checkbox";
import { Panel } from "@ui/panel";
import { Skeleton } from "@ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@ui/table";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";

declare module "@tanstack/react-table" {
  // Per-column responsive/utility classes applied by DashboardTable to the
  // body cell (`className`) and header cell (`headerClassName`). Lets columns
  // hide at breakpoints, e.g. `meta: { className: "hidden lg:table-cell" }`.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
    headerClassName?: string;
  }
}
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  applyFilters,
  applySearch,
  type DashboardFilterConfig,
  type FilterAccessor,
  type FilterPredicates,
} from "./dashboardData";
import { DashboardFilters } from "./DashboardFilters";
import { DashboardPagination } from "./DashboardPagination";
import { DashboardToolbar } from "./DashboardToolbar";
import { useDashboardQueryState } from "./useDashboardQueryState";

export interface DashboardTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  getRowId(row: T): string;
  searchable?: boolean;
  searchKeys?: (keyof T)[];
  searchPlaceholder?: string;
  selectable?: boolean;
  bulkActions?(selected: T[]): ReactNode;
  filters?: DashboardFilterConfig[];
  /** Desktop: render the filter pills at the end of the search row. */
  filtersInline?: boolean;
  filterAccessor?: FilterAccessor<T>;
  filterPredicates?: FilterPredicates<T>;
  toolbarActions?: ReactNode;
  createButton?: ReactNode;
  onRefresh?(): void;
  stickyHeader?: boolean;
  /** Panel surface tone. Defaults to the canonical card surface. */
  surface?: "card" | "plain";
  isLoading?: boolean;
  error?: string;
  empty?: {
    title: string;
    description?: string;
    action?: ReactNode;
    secondaryAction?: ReactNode;
  };
  onRowClick?(row: T): void;
  rowClassName?(row: T): string | undefined;
  pageSizeOptions?: number[];
  /** Distinguish multiple tables on one page (separate URL params). */
  namespace?: string;
  className?: string;
}

function selectionColumn<T>(t: TFunction): ColumnDef<T, unknown> {
  return {
    id: "__select",
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        aria-label={t("select_all_rows")}
        checked={
          table.getIsSomePageRowsSelected()
            ? "indeterminate"
            : table.getIsAllPageRowsSelected()
        }
        onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label={t("select_row")}
        checked={row.getIsSelected()}
        onCheckedChange={(v) => row.toggleSelected(!!v)}
        onClick={(e) => e.stopPropagation()}
      />
    ),
  };
}

export function DashboardTable<T>(props: DashboardTableProps<T>) {
  const { t } = useTranslation();
  const query = useDashboardQueryState(props.namespace);
  const {
    search,
    filters: selectedFilters,
    sort,
    page,
    pageSize,
  } = query.state;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const rows = useMemo(() => {
    let r = applySearch(props.data, search, props.searchKeys ?? []);
    r = applyFilters(
      r,
      selectedFilters,
      props.filterAccessor,
      props.filterPredicates,
    );
    return r;
  }, [
    props.data,
    props.searchKeys,
    props.filterAccessor,
    props.filterPredicates,
    search,
    selectedFilters,
  ]);

  const columns = useMemo(
    () =>
      props.selectable
        ? [selectionColumn<T>(t), ...props.columns]
        : props.columns,
    [props.selectable, props.columns, t],
  );

  const sorting: SortingState = sort
    ? [{ id: sort.id, desc: sort.dir === "desc" }]
    : [];
  const pagination: PaginationState = { pageIndex: page - 1, pageSize };

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => props.getRowId(row),
    state: { sorting, pagination, rowSelection, columnVisibility },
    enableRowSelection: props.selectable ?? false,
    // Pagination is URL-controlled. Without this, react-table fires
    // onPaginationChange whenever `data` identity changes (e.g. callers passing
    // inline `searchKeys`/arrays recompute `rows` every render) → writes the URL
    // → re-render → recompute → infinite loop (history spam + row remounts).
    autoResetPageIndex: false,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      query.setSort(
        first ? { id: first.id, dir: first.desc ? "desc" : "asc" } : null,
      );
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(pagination) : updater;
      if (next.pageSize !== pageSize) query.setPageSize(next.pageSize);
      else query.setPage(next.pageIndex + 1);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
  const isFiltering =
    search.trim().length > 0 ||
    Object.values(selectedFilters).some((v) => v.length > 0);

  return (
    <Panel
      surface={props.surface}
      className={cn("p-0 overflow-hidden", props.className)}
    >
      <DashboardToolbar
        search={
          props.searchable
            ? {
                value: search,
                onChange: query.setSearch,
                placeholder: props.searchPlaceholder,
              }
            : undefined
        }
        filters={
          props.filters && props.filters.length > 0 ? (
            <DashboardFilters
              filters={props.filters}
              selected={selectedFilters}
              onChange={query.setFilter}
              onReset={query.reset}
            />
          ) : undefined
        }
        filtersInline={props.filtersInline}
        actions={props.toolbarActions}
        onRefresh={props.onRefresh}
        createButton={props.createButton}
        selectedCount={selectedRows.length}
        bulkActions={props.bulkActions?.(selectedRows)}
      />

      {props.error ? (
        <FeedbackState variant="error" surface="plain" title={props.error} />
      ) : props.isLoading ? (
        <div className="p-6 space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <FeedbackState
          variant={isFiltering ? "no-results" : "empty"}
          surface="plain"
          title={
            isFiltering
              ? t("ds_no_results", { defaultValue: "No results" })
              : (props.empty?.title ??
                t("ds_empty", { defaultValue: "Nothing here yet" }))
          }
          description={isFiltering ? undefined : props.empty?.description}
          primaryAction={isFiltering ? undefined : props.empty?.action}
          secondaryAction={
            isFiltering ? undefined : props.empty?.secondaryAction
          }
        />
      ) : (
        <Table>
          <TableHeader
            className={cn(
              props.stickyHeader && "sticky top-0 z-10 bg-background/95 backdrop-blur",
            )}
          >
            {table.getHeaderGroups().map((hg) => (
              <TableRow
                key={hg.id}
                className="hover:bg-transparent border-black/5 dark:border-white/5"
              >
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "label-micro h-11 px-4 text-muted-foreground",
                        header.column.columnDef.meta?.headerClassName,
                      )}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {sorted === "asc" ? (
                            <ChevronUp size={12} />
                          ) : sorted === "desc" ? (
                            <ChevronDown size={12} />
                          ) : (
                            <ChevronsUpDown size={12} className="opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? "selected" : undefined}
                className={cn(
                  "border-black/5 dark:border-white/5 hover:bg-muted/40",
                  props.onRowClick && "cursor-pointer",
                  props.rowClassName?.(row.original),
                )}
                onClick={
                  props.onRowClick
                    ? () => props.onRowClick?.(row.original)
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "px-4 py-3",
                      cell.column.columnDef.meta?.className,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <DashboardPagination
        page={page}
        pageSize={pageSize}
        total={rows.length}
        pageSizeOptions={props.pageSizeOptions}
        onPage={query.setPage}
        onPageSize={query.setPageSize}
      />
    </Panel>
  );
}
